import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { keListTagFilterHaystack } from "kontrolEdge/components/Shared/mapKeListResourceTags";
import { Cloud } from "./CloudConnectivityData";

function parseMbpsInput(raw: string): number | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, "").replace(/,/g, "");
  if (/(gbps?|g)b?$/.test(s)) {
    const n = Number(s.replace(/(gbps?|g)b?$/, ""));
    return Number.isFinite(n) ? n * 1000 : null;
  }
  if (/(mbps?|m)b?$/.test(s)) {
    const n = Number(s.replace(/(mbps?|m)b?$/, ""));
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

// Keep this mapping in this file (or import it)
const providerPretty = (p?: string | null) => {
  if (!p) return "-";
  const map: Record<string, string> = {
    consoleConnect: "Console Connect",
    equinix: "Equinix",
    megaport: "Megaport",
  };
  return map[p] || p.charAt(0).toUpperCase() + p.slice(1);
};

const normCloud = (s: string) =>
    s.toLowerCase().replace(/[()\s]/g, "");

export function makeCloudFields(
  isViewingAllCustomers: boolean
): FilterField<Cloud>[] {
  const fields: FilterField<Cloud>[] = [
    {
      label: "Connection ID",
      key: "connectionid",
      accessor: (r) => r.subscriptionId,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Parent Connection",
      key: "parentconnection",
      accessor: (r) => r.parentConnection,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Name",
      key: "name",
      accessor: (r) => r.name,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Provider",
      key: "provider",
      accessor: (r) => providerPretty(r.provider),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Origin",
      key: "origin",
      accessor: (r) => r.kontrolEdgePort?.datacenter?.name ?? "",
      type: "string",
      inFreeText: true,
    },
    {
      label: "Tag",
      key: "tag",
      accessor: (r) => keListTagFilterHaystack(r),
      type: "string",
      inFreeText: true,
    },
    {
      label: "Mbps",
      key: "mbps",
      accessor: (r) => r.mbpsNum,
      type: "number",
      inFreeText: true,
      parseRhs: parseMbpsInput,
    },
    {
      label: "VLAN",
      key: "vlan",
      accessor: (r) => r.vlan,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Cloud (Account)",
      key: "cloud",
      accessor: (r) => r.cloud,
      type: "string",
      inFreeText: true,
      parseRhs: (raw) => normCloud(String(raw)),
    },
    {
      label: "Cloud Conn. ID",
      key: "cloudconnid",
      accessor: (r) => r.connectionCloudId ?? "",
      type: "string",
      inFreeText: true,
      parseRhs: (raw) => String(raw).toLowerCase().replace(/\s+/g, ""),
    },
  ];

  if (isViewingAllCustomers) {
    fields.splice(1, 0, {
      label: "Customer",
      key: "customer",
      accessor: (r) => [
        r.customer?.name ?? "",
        r.customer?.chargebeeCustomerId ?? ""
      ].filter(Boolean).join(" "),
      type: "string",
      inFreeText: true,
    });
  }

  // Hidden catch-all for free-text
  fields.push({
    label: "SearchText",
    key: "__search",
    accessor: (r) =>
      [
        r.subscriptionId,
        r.name,
        r.cloud,
        r.vlan,
        providerPretty(r.provider),
        r.kontrolEdgePort?.datacenter?.name,
        r.parentConnection,
        r.connectionCloudId,
        keListTagFilterHaystack(r),
        r.customer?.name,
      ]
        .filter(Boolean)
        .join(" "),
    type: "string",
    inFreeText: true,
  });

  for (const f of fields) f.key ||= labelId(f.label);
  return fields;
}
